import React, { useEffect, useState } from 'react';
import { getCart, updateQty, removeItem, clearCart, updateItemServices, updateItemTradeIn } from '../services/cartService';
import { getTradeInCampaign, evaluateItemCampaign } from '../services/campaignService';
import API_BASE_URL from '../config';
import toast from 'react-hot-toast';
import { useCurrency } from '../context/CurrencyContext';
import { Link, useNavigate } from 'react-router-dom';
import { getProductById } from '../services/productService';
import { getCategories } from '../services/categoryService';
import { priceInfo } from '../utils/priceUtils';

export default function CartPage() {
  const { currency, formatPrice, conversionNotice } = useCurrency();
  const isForeignCurrency = currency === 'EUR' || currency === 'USD';
  const noticeText = conversionNotice || 'Konvertimi ne EUR/USD eshte vetem informes. Fatura do te leshohet ne Leke';
  const [items, setItems] = useState([]);
  const [servicesMap, setServicesMap] = useState({});
  const [productMap, setProductMap] = useState({});
  const [categoryTree, setCategoryTree] = useState([]);
  const [servicesModalOpen, setServicesModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [modalSelectedIds, setModalSelectedIds] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const navigate = useNavigate();

  // Load active trade-in campaign
  useEffect(() => {
    let mounted = true;
    getTradeInCampaign()
      .then((camp) => {
        if (mounted) setCampaign(camp);
      })
      .catch(() => { });

    const handleUpdate = (e) => {
      if (e?.detail?.campaign) {
        setCampaign(e.detail.campaign);
      } else {
        getTradeInCampaign(true).then((camp) => {
          if (mounted) setCampaign(camp);
        }).catch(() => { });
      }
    };

    window.addEventListener('tradeInCampaignUpdated', handleUpdate);

    return () => {
      mounted = false;
      window.removeEventListener('tradeInCampaignUpdated', handleUpdate);
    };
  }, []);

  // Load cart items on mount & listen to storage / cartUpdated events
  useEffect(() => {
    let mounted = true;

    async function load() {
      const data = await getCart();
      if (mounted) setItems(data);
    }

    load();

    const onStorage = async () => {
      const d = await getCart();
      if (mounted) setItems(d);
    };

    window.addEventListener('storage', onStorage);

    return () => {
      mounted = false;
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Fetch product metadata & categories ONLY when the set of product IDs changes (add/remove), NOT on checkbox toggle or qty!
  const itemsProductIdsKey = (items || []).map((it) => String(it.productId || it.id)).sort().join(',');

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const ids = Array.from(new Set((items || []).map((it) => it.productId || it.id))).filter(Boolean);
        if (!ids.length) return;

        // Fetch categories once if not loaded
        let cats = categoryTree;
        if (!cats || !cats.length) {
          cats = await getCategories().catch(() => []);
          if (mounted && Array.isArray(cats) && cats.length) {
            setCategoryTree(cats);
          }
        }

        // Only fetch products that are not yet cached in productMap
        const missingIds = ids.filter((pid) => !productMap[pid]);
        if (missingIds.length > 0) {
          const prods = await Promise.all(
            missingIds.map((pid) => getProductById(pid).catch(() => null))
          );

          if (!mounted) return;

          const newPMap = {};
          const newSMap = {};
          prods.forEach((p) => {
            if (p && p.id) {
              newPMap[p.id] = p;
              newPMap[String(p.id)] = p;
              newSMap[p.id] = Array.isArray(p.services) ? p.services : [];
              newSMap[String(p.id)] = newSMap[p.id];
            }
          });

          setProductMap((prev) => ({ ...prev, ...newPMap }));
          setServicesMap((prev) => ({ ...prev, ...newSMap }));
        }
      } catch (_) { }
    }

    if (items && items.length > 0) loadData();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsProductIdsKey]);

  const handleQty = async (id, qty) => {
    const num = Math.max(0, Number(qty) || 0);
    // Optimistic update preserving current order
    setItems((prev) =>
      prev.map((it) => (String(it.id) === String(id) ? { ...it, qty: num } : it))
    );
    try {
      const next = await updateQty(id, num);
      if (Array.isArray(next)) {
        if (num <= 0) {
          setItems(next);
        } else {
          // Sync items with backend while strictly preserving UI order
          setItems((prev) => {
            const nextMap = new Map((next || []).map((n) => [String(n.id), n]));
            return prev
              .filter((it) => nextMap.has(String(it.id)))
              .map((it) => ({ ...it, ...nextMap.get(String(it.id)) }));
          });
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Dështoi përditësimi i sasisë');
    }
  };

  const handleRemove = async (id) => {
    try {
      const next = await removeItem(id);
      setItems(next);
    } catch (err) {
      console.error(err);
      toast.error('Dështoi heqja e produktit');
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
      setItems([]);
      toast.success('Shporta u pastrua');
    } catch (err) {
      console.error(err);
      toast.error('Dështoi pastrimi i shportës');
    }
  };

  const handleToggleTradeIn = (itemId, checked) => {
    const targetStr = String(itemId);
    // 1. Optimistically update local React state strictly for this target item
    setItems((prev) =>
      prev.map((it) => {
        const matches = String(it.productId || it.id) === targetStr || String(it.id) === targetStr;
        return matches ? { ...it, tradeIn: checked, hasTradeIn: checked } : it;
      })
    );
    // 2. Persist to storage & backend strictly for this product
    updateItemTradeIn(itemId, checked);
  };

  const handleRemoveService = async (item, svc) => {
    const newServices = (item.services || []).filter(
      (s) => String((s && s.id) || s) !== String((svc && svc.id) || svc)
    );

    const optimistic = items.map((it) => {
      if (
        it === item ||
        (String(it.id) === String(item.id) &&
          JSON.stringify(it.services || []) === JSON.stringify(item.services || []))
      ) {
        return {
          ...it,
          services: newServices,
        };
      }
      return it;
    });
    setItems(optimistic);

    try {
      const next = await updateItemServices(item, newServices);
      setItems(next);
    } catch (err) {
      console.error('Failed to remove service', err);
      toast.error('Dështoi heqja e shërbimit');
      try {
        const d = await getCart();
        setItems(d);
      } catch (_) { }
    }
  };

  const closeServicesModal = () => {
    setServicesModalOpen(false);
    setModalItem(null);
    setModalSelectedIds([]);
  };

  const toggleModalServiceId = (sId) => {
    const str = String(sId);
    setModalSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(str)) next.delete(str);
      else next.add(str);
      return Array.from(next);
    });
  };

  const saveModalServices = async () => {
    if (!modalItem) return closeServicesModal();

    try {
      const available = servicesMap[modalItem.id] || [];

      const newServices = available
        .filter((s) => modalSelectedIds.includes(String(s.id)))
        .map((s) => ({
          id: s.id,
          name: s.name,
          price: Number(s.price || 0),
        }));

      const next = await updateItemServices(modalItem, newServices);
      setItems(next);
      closeServicesModal();
    } catch (err) {
      console.error('Failed to save services', err);
      toast.error(err && err.message ? err.message : 'Dështoi përditësimi i shërbimeve');

      try {
        const d = await getCart();
        setItems(d);
      } catch (_) { }

      closeServicesModal();
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const getEffectiveItemPrice = (it) => {
    const p = productMap[it.productId] || productMap[it.id];
    const servicesSum = Array.isArray(it.services)
      ? it.services.reduce((ss, sv) => ss + Number((sv && sv.price) || 0), 0)
      : 0;

    if (it.isBundle) {
      return {
        effectivePrice: Number(it.price || 0),
        originalPrice: Number(it.price || 0),
        hasNormalDiscount: false,
        servicesSum: 0,
      };
    }

    if (p) {
      const info = priceInfo(p);
      return {
        effectivePrice: info.display,
        originalPrice: info.original,
        hasNormalDiscount: info.discounted,
        servicesSum,
      };
    }

    const basePrice =
      it.productPrice !== undefined && it.productPrice !== null
        ? Number(it.productPrice)
        : Number(it.price || 0) - servicesSum;

    return {
      effectivePrice: basePrice,
      originalPrice: basePrice,
      hasNormalDiscount: false,
      servicesSum,
    };
  };

  const totalItems = items.reduce((sum, it) => {
    return sum + Number(it.qty || 0);
  }, 0);

  // Calculate totals and trade-in savings across all items
  let grandTotal = 0;
  let campaignSavings = 0;

  items.forEach((it) => {
    const ev = evaluateItemCampaign({
      item: it,
      campaign,
      product: productMap[it.productId] || productMap[it.id],
      categoryTree,
    });
    const { effectivePrice, servicesSum } = getEffectiveItemPrice(it);

    if (ev.isEligible && !ev.isExcluded) {
      const origSub = (ev.unitInitialPrice + servicesSum) * Number(it.qty || 1);
      if (it.tradeIn) {
        grandTotal += ev.fullSubtotal;
        campaignSavings += Math.max(0, origSub - ev.fullSubtotal);
      } else {
        grandTotal += ev.baseSubtotal;
        campaignSavings += Math.max(0, origSub - ev.baseSubtotal);
      }
    } else {
      const normalSub = (effectivePrice + servicesSum) * Number(it.qty || 1);
      grandTotal += normalSub;
    }
  });

  if (!items || items.length === 0) {
    return (
      <div className="page-container" style={{ maxWidth: 1040, margin: '0 auto', padding: '32px 20px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Shporta juaj</h2>
        <div style={{ padding: '32px 24px', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', color: '#64748b' }}>
          Shporta juaj është bosh.{' '}
          <Link to="/products" style={{ color: '#00738e', fontWeight: 600, textDecoration: 'none' }}>
            Shfletoni produktet &rarr;
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ maxWidth: 1040, margin: '0 auto', padding: '24px 20px' }}>
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.3px' }}>
          Shporta ime
        </h1>
        <div style={{ color: '#94a3b8', fontSize: 18, cursor: 'pointer', padding: '4px 8px' }} title="Opsione">
          •••
        </div>
      </div>

      {/* ============================================================== */}
      {/* DESKTOP VIEW (matches images media_1790705713123.jpg & 169.jpg) */}
      {/* ============================================================== */}
      <div className="cart-desktop-view">
        {/* Table Header Pill */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(340px, 1fr) 130px 100px 160px',
            alignItems: 'center',
            background: '#ebf5f8',
            borderRadius: 8,
            padding: '11px 20px',
            marginBottom: 14,
            color: '#094859',
            fontSize: 13.5,
            fontWeight: 700,
            letterSpacing: '-0.2px',
          }}
        >
          <div>Produkti dhe përshkrimi</div>
          <div style={{ textAlign: 'center' }}>Çmimi</div>
          <div style={{ textAlign: 'center' }}>Sasia</div>
          <div style={{ textAlign: 'right', paddingRight: 38 }}>Nëntotali</div>
        </div>

        {/* Products List */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((it) => {
            const campEval = evaluateItemCampaign({
              item: it,
              campaign,
              product: productMap[it.productId] || productMap[it.id],
              categoryTree,
            });
            const { effectivePrice, originalPrice, hasNormalDiscount, servicesSum } =
              getEffectiveItemPrice(it);

            const isCampaignItem = campEval.isEligible && !campEval.isExcluded;
            const isChecked = Boolean(it.tradeIn && isCampaignItem);

            const origUnit = isCampaignItem ? (campEval.unitInitialPrice + servicesSum) : (originalPrice + servicesSum);
            const displayUnitPrice = isCampaignItem
              ? (isChecked ? campEval.unitFullPriceWithServices : campEval.unitBasePriceWithServices)
              : (effectivePrice + servicesSum);
            const displayUnitOrig = origUnit > displayUnitPrice ? origUnit : null;

            const subtotalEffective = isCampaignItem
              ? (isChecked ? campEval.fullSubtotal : campEval.baseSubtotal)
              : (effectivePrice + servicesSum) * Number(it.qty || 1);
            const subtotalOrig = origUnit * Number(it.qty || 1);
            const displaySubtotalOrig = subtotalOrig > subtotalEffective ? subtotalOrig : null;

            return (
              <div
                key={it.orderItemId ? `item-${it.orderItemId}` : `item-${it.productId || it.id}`}
                style={{
                  padding: '18px 20px',
                  borderBottom: '1px solid #f1f5f9',
                  background: '#ffffff',
                }}
              >
                {/* 4-column row matching header */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(340px, 1fr) 130px 100px 160px',
                    alignItems: 'flex-start',
                  }}
                >
                  {/* Column 1: Product Icon/Image + Info */}
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', minWidth: 0 }}>
                    {/* TV / Product Icon Container matching mockup */}
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        background: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        overflow: 'hidden',
                        padding: 2,
                      }}
                    >
                      {it.image && it.image !== 'null' ? (
                        <img
                          src={
                            it.image.startsWith('http')
                              ? it.image
                              : `${API_BASE_URL.replace(/api$/, '')}${it.image}`
                          }
                          alt={it.name}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#64748b"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
                          <polyline points="17 2 12 7 7 2" />
                        </svg>
                      )}
                    </div>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 14.5,
                          color: '#0f172a',
                          lineHeight: 1.35,
                          letterSpacing: '-0.2px',
                        }}
                      >
                        {it.name}
                      </div>
                      <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                        {it.sku || (productMap[it.productId] && productMap[it.productId].sku) || (productMap[it.id] && productMap[it.id].sku)}
                      </div>

                      {/* Campaign Badge */}
                      {isCampaignItem ? (
                        <div style={{ marginTop: 6 }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 9px',
                              background: '#dcfce7',
                              color: '#15803d',
                              borderRadius: 12,
                              fontSize: 11.5,
                              fontWeight: 700,
                            }}
                          >
                            {isChecked
                              ? `Fushata “Sill te vjetren merr te rene” : ulje ${campEval.fullDiscount}% (me pajisje te vjeter)`
                              : `Fushata “Sill te vjetren merr te rene” : ulje ${campEval.baseDiscount}% (pa pajisje te vjeter)`}
                          </span>
                        </div>
                      ) : (campaign?.enabled && campEval?.isExcluded && campaign?.showExcludedNote !== false) ? (
                        <div style={{ marginTop: 6 }}>
                          <span
                            style={{
                              display: 'inline-block',
                              fontSize: 12,
                              color: '#64748b',
                              fontWeight: 500,
                            }}
                          >
                            {campaign?.excludedNoteText || 'Nuk perfshihet ne fushaten “Sill te vjetren merr te rene”'}
                          </span>
                        </div>
                      ) : null}

                      {/* Services list */}
                      {it.services && it.services.length ? (
                        <div style={{ marginTop: 8, fontSize: 12.5 }}>
                          <strong style={{ color: '#334155' }}>Shërbimet:</strong>
                          <ul style={{ margin: '4px 0 0 14px', padding: 0 }}>
                            {it.services.map((svc, idx) => {
                              const sObj =
                                typeof svc === 'object'
                                  ? svc
                                  : (servicesMap[it.id] || []).find((s) => s.id === svc) || {
                                    id: svc,
                                  };

                              return (
                                <li
                                  key={idx}
                                  style={{
                                    display: 'flex',
                                    gap: 6,
                                    alignItems: 'center',
                                    marginBottom: 3,
                                    color: '#475569',
                                  }}
                                >
                                  <span>
                                    {sObj.name || 'Shërbim'} -{' '}
                                    {sObj.price ? formatPrice(sObj.price) : 'Falas'}
                                  </span>

                                  {sObj.price && Number(sObj.price) > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveService(it, sObj)}
                                      title="Hiq shërbimin"
                                      style={{
                                        color: '#ef4444',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: 2,
                                        display: 'inline-flex',
                                      }}
                                    >
                                      &times;
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : null}

                      {/* Add service button */}
                      {!it.isBundle &&
                        Array.isArray(servicesMap[it.id]) &&
                        servicesMap[it.id].length ? (
                        <div style={{ marginTop: 6 }}>
                          <button
                            type="button"
                            onClick={() => {
                              setModalItem(it);
                              setModalSelectedIds(
                                (it.services || []).map((s) => String((s && s.id) || s))
                              );
                              setServicesModalOpen(true);
                            }}
                            style={{
                              background: 'transparent',
                              color: '#00738e',
                              border: 'none',
                              padding: '2px 0',
                              margin: 0,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 12.5,
                              fontWeight: 600,
                            }}
                          >
                            + Shto shërbim
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Column 2: Çmimi */}
                  <div style={{ textAlign: 'center', paddingTop: 2 }}>
                    {displayUnitOrig && (
                      <div
                        style={{
                          textDecoration: 'line-through',
                          color: '#94a3b8',
                          fontSize: 12.5,
                          fontWeight: 400,
                          lineHeight: 1.2,
                        }}
                      >
                        {formatPrice(displayUnitOrig)}
                      </div>
                    )}
                    <div
                      style={{
                        color: isChecked || hasNormalDiscount ? '#15803d' : '#0f172a',
                        fontWeight: 700,
                        fontSize: 15.5,
                        letterSpacing: '-0.2px',
                      }}
                    >
                      {formatPrice(displayUnitPrice)}
                    </div>
                  </div>

                  {/* Column 3: Sasia */}
                  <div style={{ textAlign: 'center', paddingTop: 0 }}>
                    <input
                      type="number"
                      min={0}
                      value={it.qty}
                      onChange={(e) => handleQty(it.id, e.target.value)}
                      style={{
                        width: 48,
                        height: 34,
                        textAlign: 'center',
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 14,
                        color: '#0f172a',
                        outline: 'none',
                      }}
                    />
                  </div>

                  {/* Column 4: Nëntotali & Trash Button */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 14,
                      paddingTop: 2,
                    }}
                  >
                    <div style={{ textAlign: 'right' }}>
                      {displaySubtotalOrig && (
                        <div
                          style={{
                            textDecoration: 'line-through',
                            color: '#94a3b8',
                            fontSize: 12.5,
                            fontWeight: 400,
                            lineHeight: 1.2,
                          }}
                        >
                          {formatPrice(displaySubtotalOrig)}
                        </div>
                      )}
                      <div
                        style={{
                          color: isChecked || hasNormalDiscount ? '#15803d' : '#0f172a',
                          fontWeight: 700,
                          fontSize: 15.5,
                          letterSpacing: '-0.2px',
                        }}
                      >
                        {formatPrice(subtotalEffective)}
                      </div>
                    </div>

                    {/* Circular Peach Trash Button */}
                    <button
                      onClick={() => handleRemove(it.id)}
                      aria-label="Hiq produktin"
                      title="Hiq produktin"
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: '#ffedd5',
                        border: 'none',
                        color: '#ea580c',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'transform 0.15s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Wide Dashed Trade-In Container across bottom of item matching Images 2 & 3 */}
                {campEval.isEligible && !campEval.isExcluded && (
                  <div
                    style={{
                      marginTop: 14,
                      marginLeft: 64, // Aligns neatly under the product title/badge
                      border: '1.5px dashed #cbd5e1',
                      borderRadius: 10,
                      padding: '12px 18px',
                      background: '#ffffff',
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        cursor: 'pointer',
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => handleToggleTradeIn(it.productId || it.id, e.target.checked)}
                        style={{
                          width: 18,
                          height: 18,
                          marginTop: 2,
                          accentColor: '#00738e',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 13.5,
                            color: '#0f172a',
                            lineHeight: 1.3,
                          }}
                        >
                          {campaign?.checkboxLabel || 'Kam pajisje të vjetër për ta dorëzuar'}
                        </div>
                        <div
                          style={{
                            fontSize: 12.5,
                            color: '#475569',
                            marginTop: 3,
                            lineHeight: 1.45,
                          }}
                        >
                          Sill të vjetrën, merr të renë: ulja rritet nga {campEval.baseDiscount}% në {campEval.fullDiscount}%. Pajisja dorëzohet gjatë marrjes ose dërgesës së produktit.
                        </div>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop Totali & Actions Section */}
        <div style={{ marginTop: 24, paddingTop: 16 }}>
          {/* Totali Row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(340px, 1fr) 130px 100px 160px',
              alignItems: 'center',
              padding: '0 20px',
              marginBottom: 6,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Totali</div>
            <div></div>
            <div
              style={{
                textAlign: 'center',
                fontSize: 16,
                fontWeight: 600,
                color: '#0f172a',
              }}
            >
              {totalItems}
            </div>
            <div
              style={{
                textAlign: 'right',
                fontSize: 22,
                fontWeight: 700,
                color: '#0f172a',
                paddingRight: 46,
              }}
            >
              {formatPrice(grandTotal)}
            </div>
          </div>

          {/* Foreign Currency Conversion Notice (EUR / USD) */}
          {isForeignCurrency && noticeText && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 8,
                padding: '8px 14px',
                margin: '8px 46px 14px auto',
                width: 'fit-content',
                maxWidth: '600px',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: 8,
                color: '#92400e',
                fontSize: 12.5,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{noticeText}</span>
            </div>
          )}

          {/* Campaign Savings Notice */}
          {campaignSavings > 0 && (
            <div
              style={{
                textAlign: 'right',
                fontSize: 13,
                color: '#475569',
                paddingRight: 20,
                marginBottom: 16,
              }}
            >
              Kurseni {formatPrice(campaignSavings)} në produktin e fushatës
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 12,
              paddingRight: 20,
            }}
          >
            <button
              onClick={handleClearCart}
              style={{
                background: '#ffedd5',
                color: '#c2410c',
                border: '1px solid #fed7aa',
                padding: '10px 22px',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#fed7aa')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#ffedd5')}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
              <span>Pastro</span>
            </button>

            <button
              onClick={handleCheckout}
              style={{
                background: '#00738e',
                color: '#ffffff',
                border: 'none',
                padding: '10px 28px',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14.5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#09586c')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#00738e')}
            >
              <span>Vazhdo</span>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ============================================================== */}
      {/* MOBILE VIEW (matches image media_1790705713082.jpg)            */}
      {/* ============================================================== */}
      <div className="cart-mobile-view" style={{ maxWidth: 540, margin: '0 auto' }}>
        {items.map((it) => {
          const campEval = evaluateItemCampaign({
            item: it,
            campaign,
            product: productMap[it.productId] || productMap[it.id],
            categoryTree,
          });
          const { effectivePrice, originalPrice, hasNormalDiscount, servicesSum } =
            getEffectiveItemPrice(it);

          const isCampaignItem = campEval.isEligible && !campEval.isExcluded;
          const isChecked = Boolean(it.tradeIn && isCampaignItem);

          const origUnit = isCampaignItem ? (campEval.unitInitialPrice + servicesSum) : (originalPrice + servicesSum);
          const displayUnitPrice = isCampaignItem
            ? (isChecked ? campEval.unitFullPriceWithServices : campEval.unitBasePriceWithServices)
            : (effectivePrice + servicesSum);
          const displayUnitOrig = origUnit > displayUnitPrice ? origUnit : null;

          const subtotalEffective = isCampaignItem
            ? (isChecked ? campEval.fullSubtotal : campEval.baseSubtotal)
            : (effectivePrice + servicesSum) * Number(it.qty || 1);
          const subtotalOrig = origUnit * Number(it.qty || 1);
          const displaySubtotalOrig = subtotalOrig > subtotalEffective ? subtotalOrig : null;

          return (
            <div
              key={it.orderItemId ? `m-item-${it.orderItemId}` : `m-item-${it.productId || it.id}`}
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: 16,
                marginBottom: 16,
              }}
            >
              {/* Product Header: Icon + Title + SKU + Badge */}
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    background: '#fafafa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                    padding: 2,
                  }}
                >
                  {it.image && it.image !== 'null' ? (
                    <img
                      src={
                        it.image.startsWith('http')
                          ? it.image
                          : `${API_BASE_URL.replace(/api$/, '')}${it.image}`
                      }
                      alt={it.name}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
                      <polyline points="17 2 12 7 7 2" />
                    </svg>
                  )}
                </div>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 14.5,
                      color: '#0f172a',
                      lineHeight: 1.35,
                      letterSpacing: '-0.2px',
                    }}
                  >
                    {it.name}
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                    {it.sku || (productMap[it.productId] && productMap[it.productId].sku) || (productMap[it.id] && productMap[it.id].sku)}
                  </div>

                  {/* Campaign Badge */}
                  {isCampaignItem ? (
                    <div style={{ marginTop: 6 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          background: '#dcfce7',
                          color: '#15803d',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {isChecked
                          ? `Fushata “Sill te vjetren merr te rene” : ulje ${campEval.fullDiscount}% (me pajisje te vjeter)`
                          : `Fushata “Sill te vjetren merr te rene” : ulje ${campEval.baseDiscount}% (pa pajisje te vjeter)`}
                      </span>
                    </div>
                  ) : (campaign?.enabled && campEval?.isExcluded && campaign?.showExcludedNote !== false) ? (
                    <div style={{ marginTop: 6 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          fontSize: 11.5,
                          color: '#64748b',
                          fontWeight: 500,
                        }}
                      >
                        {campaign?.excludedNoteText || 'Nuk perfshihet ne fushaten “Sill te vjetren merr te rene”'}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Dashed Trade-in Box matching Image 1 */}
              {isCampaignItem && (
                <div
                  style={{
                    border: '1px dashed #cbd5e1',
                    borderRadius: 8,
                    padding: '12px 14px',
                    marginBottom: 12,
                    background: '#ffffff',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      cursor: 'pointer',
                      margin: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => handleToggleTradeIn(it.productId || it.id, e.target.checked)}
                      style={{
                        width: 18,
                        height: 18,
                        marginTop: 2,
                        accentColor: '#00738e',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13.5,
                          color: '#0f172a',
                          lineHeight: 1.3,
                        }}
                      >
                        {campaign?.checkboxLabel || 'Kam pajisje të vjetër për ta dorëzuar'}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#64748b',
                          marginTop: 4,
                          lineHeight: 1.4,
                        }}
                      >
                        Ulja rritet nga {campEval.baseDiscount}% në {campEval.fullDiscount}%. Pajisja dorëzohet gjatë marrjes ose dërgesës.
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {/* Price Row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 0',
                  borderTop: '1px solid #f1f5f9',
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Price</span>
                <div style={{ textAlign: 'right' }}>
                  {displayUnitOrig && (
                    <div
                      style={{
                        textDecoration: 'line-through',
                        color: '#94a3b8',
                        fontSize: 12,
                        lineHeight: 1.2,
                      }}
                    >
                      {formatPrice(displayUnitOrig)}
                    </div>
                  )}
                  <div
                    style={{
                      color: isChecked || hasNormalDiscount ? '#15803d' : '#0f172a',
                      fontWeight: 700,
                      fontSize: 14.5,
                    }}
                  >
                    {formatPrice(displayUnitPrice)}
                  </div>
                </div>
              </div>

              {/* Qty Row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 0',
                  borderTop: '1px solid #f1f5f9',
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Qty</span>
                <input
                  type="number"
                  min={0}
                  value={it.qty}
                  onChange={(e) => handleQty(it.id, e.target.value)}
                  style={{
                    width: 48,
                    height: 34,
                    textAlign: 'center',
                    border: '1px solid #cbd5e1',
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#0f172a',
                    background: '#ffffff',
                    outline: 'none',
                  }}
                />
              </div>

              {/* Subtotal Row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 0',
                  borderTop: '1px solid #f1f5f9',
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Subtotal</span>
                <div style={{ textAlign: 'right' }}>
                  {displaySubtotalOrig && (
                    <div
                      style={{
                        textDecoration: 'line-through',
                        color: '#94a3b8',
                        fontSize: 12,
                        lineHeight: 1.2,
                      }}
                    >
                      {formatPrice(displaySubtotalOrig)}
                    </div>
                  )}
                  <div
                    style={{
                      color: isChecked || hasNormalDiscount ? '#15803d' : '#0f172a',
                      fontWeight: 700,
                      fontSize: 14.5,
                    }}
                  >
                    {formatPrice(subtotalEffective)}
                  </div>
                </div>
              </div>

              {/* Action Row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 0',
                  borderTop: '1px solid #f1f5f9',
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Action</span>
                <button
                  onClick={() => handleRemove(it.id)}
                  aria-label="Hiq produktin"
                  title="Hiq produktin"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: '#ffedd5',
                    border: 'none',
                    color: '#ea580c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}

        {/* Mobile Summary */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 6,
              fontSize: 13,
              color: '#475569',
            }}
          >
            <span>
              {totalItems} {totalItems === 1 ? 'produkt' : 'produkte'}
            </span>
            {campaignSavings > 0 && <span>Kurseni {formatPrice(campaignSavings)}</span>}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: isForeignCurrency && noticeText ? 8 : 16,
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Totali</span>
            <span style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
              {formatPrice(grandTotal)}
            </span>
          </div>

          {/* Foreign Currency Conversion Notice (EUR / USD) */}
          {isForeignCurrency && noticeText && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                marginBottom: 16,
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: 8,
                color: '#92400e',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{noticeText}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={handleClearCart}
              style={{
                background: '#ffedd5',
                color: '#c2410c',
                border: '1px solid #fed7aa',
                padding: '10px 20px',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
              <span>Pastro</span>
            </button>

            <button
              onClick={handleCheckout}
              style={{
                background: '#00738e',
                color: '#ffffff',
                border: 'none',
                padding: '10px 24px',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14.5,
                flex: 1,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <span>Vazhdo</span>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Services Selection Modal */}
      {servicesModalOpen && modalItem && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 120,
          }}
        >
          <div
            className="modal-card"
            style={{
              width: 580,
              maxHeight: '80vh',
              overflow: 'auto',
              background: '#fff',
              padding: 20,
              borderRadius: 12,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                Shto shërbim për {modalItem.name}
              </h4>
              <button
                onClick={closeServicesModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  cursor: 'pointer',
                  color: '#64748b',
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              {(servicesMap[modalItem.id] || []).map((svc) => {
                const isFree = !svc.price || Number(svc.price) === 0;
                const checked = !isFree && modalSelectedIds.includes(String(svc.id));

                return (
                  <label
                    key={svc.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      border: '1px solid #e2e8f0',
                      padding: 10,
                      borderRadius: 8,
                      cursor: isFree ? 'default' : 'pointer',
                    }}
                  >
                    {!isFree && (
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleModalServiceId(svc.id)}
                        style={{ accentColor: '#00738e' }}
                      />
                    )}

                    <div style={{ flex: 1, paddingLeft: isFree ? '24px' : '0' }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{svc.name}</div>
                      {svc.description ? (
                        <div style={{ color: '#64748b', fontSize: 12.5, marginTop: 2 }}>
                          {svc.description}
                        </div>
                      ) : null}
                    </div>

                    <div style={{ fontWeight: 700, color: isFree ? '#64748b' : '#0f172a', fontSize: 14 }}>
                      {isFree ? 'Falas' : formatPrice(svc.price)}
                    </div>
                  </label>
                );
              })}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
                <button
                  onClick={closeServicesModal}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    color: '#475569',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Anulo
                </button>
                <button
                  onClick={saveModalServices}
                  style={{
                    padding: '8px 18px',
                    background: '#00738e',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Ruaj
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
